// glide-ui: 5
//
// Nav — responsive app nav. Desktop: sticky full-bleed top bar (translucent backdrop),
// or a fixed left sidebar (w-56) with layout="sidebar" — sidebar pages need md:pl-56.
// Mobile: floating pill at the bottom (both layouts).
// items: { href, label, icon }[]. Brand mark + title are read from the page head
// (favicon + the app name) so they stay in sync with the document / home-screen title.
// Returns null when items.length <= 1 — single-page apps don't render a nav.
// Uses react-router-dom Link + useLocation for active state.
// Desktop top bar is sticky (takes its own space in flow). Sidebar is fixed — page
// needs md:pl-56. Mobile pill floats below md; the app shell pads #root for it
// automatically (keyed on data-slot="nav-mobile") — pages add no pb-* for the nav.
// Labels never wrap. Desktop item rows scroll horizontally (scrollbar hidden) on
// overflow. The mobile pill never scrolls at <= 5 items — they split the pill
// evenly, expanding to fill the screen up to 5rem each and truncating their
// labels below that, so all of them stay visible. Above 5 it scrolls, sized so
// the 5th item is exactly half-visible: the peek is what advertises the scroll,
// and whichever edge still has content behind it fades rather than hard-cutting.
// <Nav items={[{ href: "/", label: "Home", icon: <Home /> }]} />
// <Nav layout="sidebar" items={...} />

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@glide/cn";

export interface NavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
}

export interface NavProps {
  items: NavItem[];
  layout?: "top" | "sidebar";
  className?: string;
}

export function Nav({ items, layout = "top", className }: NavProps) {
  const { pathname } = useLocation();
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    setFaviconUrl(link?.href ?? null);
  }, []);

  // Which ends of the mobile row still have content scrolled past them.
  // Starts "both clear" so a non-scrolling nav never flashes a fade.
  const rowRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ atStart: true, atEnd: true });

  const syncEdges = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
    setEdges((prev) =>
      prev.atStart === atStart && prev.atEnd === atEnd
        ? prev
        : { atStart, atEnd },
    );
  }, []);

  useEffect(() => {
    syncEdges();
    window.addEventListener("resize", syncEdges);
    return () => window.removeEventListener("resize", syncEdges);
  }, [syncEdges, items.length]);

  if (items.length <= 1) return null;

  // Header label = the document title (set from the app's name), so it stays
  // in sync with the browser tab + home-screen title.
  const title = document.title || null;

  const isActive = (href: string) =>
    pathname === href || (href !== "/" && pathname.startsWith(href + "/"));

  // Above 5 destinations the mobile pill scrolls with a half-item peek.
  const peeks = items.length > 5;

  // Dissolve whichever edge still has content behind it, so the peeking item
  // reads as "scroll for more" rather than as layout cut off at the pill's
  // border. Both ends clear (the <= 5 case, which never scrolls) — no mask.
  const fadeMask =
    edges.atStart && edges.atEnd
      ? undefined
      : `linear-gradient(to right, ${
          edges.atStart ? "black 0" : "transparent 0, black 2rem"
        }, ${
          edges.atEnd
            ? "black 100%"
            : "black calc(100% - 2rem), transparent 100%"
        })`;

  // max-w-full clamps the title to its container: the brand grid column in the
  // top bar (floored at 14rem by the grid template, so items can't crush it),
  // the w-56 rail in the sidebar. The span's truncate handles the rest.
  const brand = (
    <Link
      to="/"
      className="flex items-center gap-2 w-fit max-w-full outline-none focus-visible:ring-focus-ring/50 focus-visible:ring-[3px] rounded-md"
    >
      {faviconUrl && (
        <div className="flex items-center justify-center overflow-hidden shrink-0 size-8 rounded-lg">
          <img src={faviconUrl} alt="" className="size-full object-cover" />
        </div>
      )}
      {title && (
        <span className="text-base font-semibold text-primary truncate">
          {title}
        </span>
      )}
    </Link>
  );

  return (
    <>
      {layout === "top" ? (
        /* Desktop: sticky full-bleed top bar */
        <nav
          data-slot="nav"
          className={cn(
            "hidden md:block sticky top-0 z-50",
            "bg-raised/90 backdrop-blur supports-[backdrop-filter]:bg-raised/80",
            "shadow-sm",
            className,
          )}
        >
          <div className="grid grid-cols-[minmax(14rem,1fr)_minmax(0,auto)_1fr] items-center gap-4 min-h-[4rem] px-4 mx-auto max-w-[1200px]">
            {brand}
            <div className="flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {items.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap",
                      "transition-colors duration-150",
                      "outline-none focus-visible:ring-focus-ring/50 focus-visible:ring-[3px]",
                      active
                        ? "bg-accent/10 text-accent font-semibold underline decoration-2 underline-offset-4"
                        : "text-secondary hover:text-primary hover:bg-inset",
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
            <div />
          </div>
        </nav>
      ) : (
        /* Desktop: fixed left sidebar — pages need md:pl-56 */
        <nav
          data-slot="nav"
          className={cn(
            "hidden md:flex flex-col fixed inset-y-0 left-0 z-50 w-56",
            "bg-raised border-r border-border",
            className,
          )}
        >
          <div className="p-4">{brand}</div>
          <div className="flex-1 overflow-y-auto px-3 pb-4">
            <div className="flex flex-col gap-1">
              {items.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium",
                      "transition-colors duration-150",
                      "outline-none focus-visible:ring-focus-ring/50 focus-visible:ring-[3px]",
                      "[&_svg]:size-5 [&_svg]:shrink-0",
                      active
                        ? "bg-accent/10 text-accent font-semibold ring-1 ring-inset ring-current/40"
                        : "text-secondary hover:text-primary hover:bg-inset",
                    )}
                  >
                    {item.icon}
                    <span className="truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Mobile: floating bottom pill */}
      <nav
        data-slot="nav-mobile"
        style={{ "--nav-items": items.length } as React.CSSProperties}
        className={cn(
          // Sits 0.5rem up, or — where a safe-area inset exists (standalone
          // on home-indicator iPhones) — tucked to ~8px above the home
          // indicator (13px = its 5px bar + 8px below it), the geometry
          // native floating tab bars ship.
          "md:hidden fixed bottom-[max(0.5rem,env(safe-area-inset-bottom,0px)_-_13px)] left-1/2 -translate-x-1/2 z-50",
          "rounded-full bg-raised shadow-lg",
          "p-1 max-w-[calc(100%-16px)]",
          // The pill carries the per-item max width, not the items: it is
          // shrink-to-fit, and a shrink-to-fit container sizes to its items'
          // content, so items given a 5rem basis would just collapse back to
          // their labels. Sizing the pill to what N items at 5rem need (plus
          // gaps and padding) lets max-w cap it on narrow screens and the
          // even split below hand each item its share.
          !peeks &&
            "w-[calc(var(--nav-items)*5rem+(var(--nav-items)-1)*0.25rem+0.5rem)]",
        )}
      >
        <div
          ref={rowRef}
          onScroll={syncEdges}
          className="flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={
            fadeMask !== undefined
              ? { maskImage: fadeMask, WebkitMaskImage: fadeMask }
              : undefined
          }
        >
          {items.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex flex-col items-center justify-center gap-0.5",
                  "rounded-full px-2 py-1.5",
                  // 4.5 items = 4 full + 4 gaps (1rem) + the half-item peek.
                  // min-w keeps a long label from beating the automatic
                  // min-content floor and pushing its item wider.
                  peeks
                    ? "shrink-0 grow-0 basis-[calc((100%-1rem)/4.5)] min-w-0"
                    : "flex-1 basis-0 min-w-[44px]",
                  "text-[10px] font-medium",
                  "transition-colors duration-150",
                  "outline-none focus-visible:ring-focus-ring/50 focus-visible:ring-[3px]",
                  "[&_svg]:size-6 [&_svg]:shrink-0",
                  active
                    ? "bg-accent/10 text-accent font-semibold ring-1 ring-inset ring-current/40"
                    : "text-secondary hover:text-primary",
                )}
              >
                {item.icon}
                {/* Needs a clamp to truncate at all, but max-w-full not
								    w-full: a percentage width contributes nothing to
								    intrinsic sizing, which the peek branch's pill needs. */}
                <span className="max-w-full text-center truncate">
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
Nav.displayName = "Nav";
